<template>
  <RevPicker
    :aria-disabled="disabled"
    :color="color"
    :description="description"
    :disabled="disabled"
    :label="label"
    :loading="loading"
    :prefix="pickerIcon"
    :prefix-props="pickerIconProps"
    :selected="selected"
    :tag="tag"
    :to="to"
    @click="onClick"
  />
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { type LinkInternal, type Price } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { type Colors, RevPicker } from '@ds/components/Picker'

import { useUrlParams } from '../../../../composables/useUrlParams'

import ColorRound from './ColorRound/ColorRound.vue'
import { EMPHASIS_TO_COLOR, PICKER_ICONS } from './Picker.constants'
import translations from './Picker.translations'

const i18n = useI18n()
const logger = useLogger()
const { trackClick } = useTracking()
const { productId } = useUrlParams()

export type PickerProps = {
  shouldDisplaySoldOut?: boolean
  label: string
  trackingId?: string | null
  trackingValue?: string | null
  link?: LinkInternal | undefined
  available: boolean
  selected: boolean
  icon?: string
  price?: Price | null
  color?: string | null
  goodDeal?: boolean
  loading?: boolean
  emphasis?: 'default' | 'info' | 'accent'
}

const props = withDefaults(defineProps<PickerProps>(), {
  shouldDisplaySoldOut: false,
  trackingId: '',
  trackingValue: '',
  icon: '',
  link: undefined,
  price: null,
  color: null,
  goodDeal: false,
  loading: false,
  emphasis: 'default',
})

const emit = defineEmits(['click'])

const disabled = computed(() => !props.available)
const to = computed((): Location | undefined => {
  if (!props.available || !props.link) {
    return undefined
  }

  return {
    ...props.link,
    // Adding a scroll=false param to avoid transitioning when using pickers. See product.vue
    hash: {
      ...props.link.hash,
      scroll: false,
    },
  } as unknown as Location
})

const description = computed(() => {
  if (props.shouldDisplaySoldOut && disabled.value) {
    return i18n(translations.soldOut)
  }

  if (!isEmpty(props.price)) {
    return i18n.price(props.price)
  }

  return ''
})

const color = computed((): Colors | undefined => {
  return EMPHASIS_TO_COLOR[props.emphasis] as unknown as Colors
})

const tag = computed(() => {
  return props.goodDeal ? i18n(translations.goodDeal) : ''
})

const pickerIcon = computed(() => {
  if (props.icon && Object.keys(PICKER_ICONS).includes(props.icon)) {
    return PICKER_ICONS[props.icon]
  }

  if (props.color) {
    return ColorRound
  }

  // Some colors are missing from the catalog, so we don't display the color round on them. Let's log the info to
  // help the catalog team to fix them!
  if (props.color === '') {
    logger.error('[PRODUCT] Missing color code', {
      colorLabel: props.label,
      productId,
    })
  }

  return undefined
})

const pickerIconProps = computed(() => {
  if (!props.icon && props.color) {
    return { color: props.color }
  }

  return {}
})

function onClick() {
  if (disabled.value || props.selected) {
    return
  }

  // We're a bit defensive on those trackingId / trackingValue, as the API specs says that
  // both fields are optional. Let's have default values to make sure we're able to detect this
  // in amplitude no matter what
  trackClick({
    zone: props.trackingId ?? 'unknown_picker',
    name: props.trackingValue ?? props.label,
  })

  emit('click')
}
</script>
